import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  HStack,
  Image,
  Img,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Tag,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { JsonRpcProvider } from '@ethersproject/providers';
import * as ethers from 'ethers';
import NextLink from 'next/link';
import React, { useEffect, useState } from 'react';
import { useDidHydrate } from '../lib/useDidHydrate';
import { DAO_ADDRESS } from '../utils/constants';
import { DEFAULT_NETWORK, NETWORKS } from '../web3/constants';
import { formatAddress } from '../web3/helpers';
import { useENS } from '../web3/hooks/useENS';
import { useWallet } from '../web3/WalletContext';

const Navbar: React.FC = () => {
  const [daoBalance, setdaoBalance] = useState<number | null>(null);

  useEffect(() => {
    const getDaoBalance = async () => {
      const provider = new JsonRpcProvider(NETWORKS[DEFAULT_NETWORK].rpc);
      const balance = await provider.getBalance(DAO_ADDRESS);
      const balanceInEth = ethers.utils.formatEther(balance);
      setdaoBalance(Math.round(Number(balanceInEth)));
    };
    getDaoBalance();
  }, [daoBalance]);

  const { connectWallet, disconnect, address, isConnecting } = useWallet();

  const didHydrate = useDidHydrate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { ens } = useENS({ address });
  const walletLabel = ens ?? formatAddress(address);
  const links = (
    <Stack
      spacing={{ base: 4, lg: 8 }}
      align="center"
      justify="center"
      direction={{ base: 'column', lg: 'row' }}
    >
      {daoBalance && (
        <Link href={`https://etherscan.io/address/${DAO_ADDRESS}`} isExternal>
          <Tag variant="subtle" size="lg">
            Treasury: Ξ{daoBalance.toString()}
          </Tag>
        </Link>
      )}
      <NextLink href="/" passHref>
        <Link fontSize={{ base: 'xl', md: 'lg' }} onClick={onClose}>
          Home
        </Link>
      </NextLink>
      <NextLink href="/about" passHref>
        <Link fontSize={{ base: 'xl', md: 'lg' }} onClick={onClose}>
          About
        </Link>
      </NextLink>
      {/* <NextLink href="/auction" passHref>
        <Link fontSize={{ base: 'xl', md: 'lg' }} onClick={onClose}>
          Auction
        </Link>
      </NextLink>
      <NextLink href="/mint" passHref>
        <Link fontSize={{ base: 'xl', md: 'lg' }} onClick={onClose}>
          Mint
        </Link>
      </NextLink> */}
      <NextLink href="/mint-pfp" passHref>
        <Link fontSize={{ base: 'xl', md: 'lg' }} onClick={onClose}>
          Mint PFP
        </Link>
      </NextLink>
      <NextLink href="/claim" passHref>
        <Link pt={{ base: 8, md: 0 }} fontSize={{ base: 'xl', md: 'lg' }}>
          Upgrade
        </Link>
      </NextLink>
      <NextLink href="/explore" passHref>
        <Link fontSize={{ base: 'xl', md: 'lg' }} onClick={onClose}>
          Explore
        </Link>
      </NextLink>
      <NextLink href="/songadao" passHref>
        <Link fontSize={{ base: 'xl', md: 'lg' }} onClick={onClose}>
          SongADAO
        </Link>
      </NextLink>
    </Stack>
  );

  return (
    <VStack align="stretch" as="header" px="4" py="2">
      <HStack justifyContent="space-between">
        <NextLink href="/" passHref>
          <Link h={[20, 20, 24]}>
            <Img h="full" cursor="pointer" src="/assets/logo.png" alt="the Song a Day World logo" />
          </Link>
        </NextLink>
        <HStack spacing="4">
          <HStack spacing="4" display={{ base: 'none', lg: 'inherit' }} justifyContent="end">
            {links}
          </HStack>
          <Box display={{ base: 'none', lg: 'block' }}>
            {didHydrate && address ? (
              <Menu>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />}>
                  {walletLabel}
                </MenuButton>
                <MenuList>
                  <MenuItem as="div" p="0">
                    <NextLink href={`/a/${address}`} passHref>
                      <Link
                        px="3"
                        py="2"
                        display="block"
                        width="100%"
                        _hover={{ textDecoration: 'none' }}
                      >
                        My Songs
                      </Link>
                    </NextLink>
                  </MenuItem>
                  <MenuItem px="3" py="2" onClick={disconnect}>
                    Disconnect
                  </MenuItem>
                </MenuList>
              </Menu>
            ) : (
              <Button onClick={connectWallet} isLoading={isConnecting}>
                Connect Wallet
              </Button>
            )}
          </Box>
        </HStack>
        <Image
          w="12"
          h="10"
          src="/assets/menuOpen.png"
          alt="Open Menu"
          onClick={onOpen}
          aria-label="Open Menu"
          display={{ lg: 'none' }}
        />
      </HStack>

      <Drawer placement="right" onClose={onClose} isOpen={isOpen}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton>
            <Image
              w="10"
              h="8"
              src="/assets/menuClose.png"
              alt="Close Menu"
              display={{ lg: 'none' }}
            />
          </DrawerCloseButton>
          <DrawerBody mt={16} textAlign="center">
            <Box display={{ lg: 'none' }}>
              {didHydrate && address ? (
                <Menu>
                  <MenuButton as={Button} rightIcon={<ChevronDownIcon />} my={4}>
                    {walletLabel}
                  </MenuButton>
                  <MenuList>
                    <MenuItem as="div" p="0">
                      <NextLink href={`/a/${address}`} passHref>
                        <Link
                          px="3"
                          py="2"
                          display="block"
                          width="100%"
                          _hover={{ textDecoration: 'none' }}
                          onClick={onClose}
                        >
                          My Songs
                        </Link>
                      </NextLink>
                    </MenuItem>
                    <MenuItem px="3" py="2" onClick={disconnect}>
                      Disconnect
                    </MenuItem>
                  </MenuList>
                </Menu>
              ) : (
                <Button onClick={connectWallet} isLoading={isConnecting}>
                  Connect Wallet
                </Button>
              )}
            </Box>
            {links}
          </DrawerBody>
          <DrawerFooter textAlign="center">
            <Button variant="outline" mr={3} onClick={onClose}>
              Close menu
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </VStack>
  );
};

export default Navbar;
